
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuthGuard } from "@/hooks/useAuthGuard";
import { useQueryClient } from "@tanstack/react-query";

/**
 * Hook for managing resume deletion
 */
export const useResumeDeletion = () => {
  const { toast } = useToast();
  const { ensureAuthenticated } = useAuthGuard();
  const queryClient = useQueryClient();

  const handleDeleteResume = async (resume: any) => {
    if (!ensureAuthenticated()) return;

    try {
      // If this is an original resume that has optimized versions, warn the user
      if (resume.is_original) {
        const { data: childResumes, error: checkError } = await supabase
          .from('resumes')
          .select('id')
          .eq('parent_resume_id', resume.id);
          
        if (checkError) throw checkError;
        
        if (childResumes && childResumes.length > 0) {
          // We should inform the user that this will delete related optimized resumes
          toast({
            title: "Warning",
            description: `This will delete ${childResumes.length} optimized ${childResumes.length === 1 ? 'version' : 'versions'} based on this resume.`,
            variant: "destructive"
          });
        }
      }
      
      // Delete the resume from storage if it has a preview path
      if (resume.preview_content) {
        try {
          await supabase.storage
            .from('resumes')
            .remove([resume.preview_content]);
        } catch (storageError) {
          console.error("Storage removal error:", storageError);
        }
      }

      // Delete the resume record
      const { error } = await supabase
        .from('resumes')
        .delete()
        .eq('id', resume.id);

      if (error) throw error;

      // If it was an original resume, also delete all optimized versions
      if (resume.is_original) {
        try {
          await supabase
            .from('resumes')
            .delete()
            .eq('parent_resume_id', resume.id);
        } catch (error) {
          console.error("Error deleting child resumes:", error);
        }
      }

      toast({
        title: "Resume Deleted",
        description: "The resume has been successfully deleted."
      });

      // Invalidate all resume-related queries to ensure UI is updated
      await queryClient.invalidateQueries({ queryKey: ["resumes"] });
      
      // Return success to allow chaining
      return true;
    } catch (err: any) {
      toast({
        title: "Error Deleting Resume",
        description: err.message,
        variant: "destructive"
      });
      return false;
    }
  };

  return { handleDeleteResume };
};
